import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtNum, fmtCur, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Loader2, Receipt, User, Building2, Lock } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["user_id", "User / Account ID", "text"],
  ["name", "Full Name", "text"],
  ["email", "Email", "text"],
  ["phone", "Phone", "text"],
  ["country", "Country", "text"],
  ["kyc_status", "KYC Status", "text"],
  ["status", "Account Status", "text"],
  ["referred_by", "Referred By", "text"],
  ["referral_code", "Referral Code (own)", "text"],
  ["deposits", "Deposits", "number"],
  ["balance", "Balance", "number"],
  ["created_date", "Created Date", "date"],
];

const fmtAmt = (v, cur) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? `${n} ${cur}` : n;
};

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const { data, isLoading } = useQuery({
    queryKey: ["customer-profile", id],
    queryFn: () => api.get(`/customers/${id}`).then((r) => r.data),
  });

  const c = data?.customer;
  const txs = data?.transactions || [];

  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!c) return;
    const f = {};
    FIELDS.forEach(([k]) => {
      f[k] = k === "created_date" && c.created_date ? String(c.created_date).slice(0, 10) : (c[k] ?? "");
    });
    f.account_type = c.account_type || "individual";
    setForm(f);
  }, [c]);

  const dirty = c && FIELDS.some(([k]) => {
    const orig = k === "created_date" && c.created_date ? String(c.created_date).slice(0, 10) : (c[k] ?? "");
    return String(form[k] ?? "") !== String(orig);
  }) || (c && (form.account_type || "individual") !== (c.account_type || "individual"));

  const save = async () => {
    setBusy(true);
    try {
      // dirty-tracking: only send fields the user actually changed
      const body = {};
      FIELDS.forEach(([k]) => {
        const orig = k === "created_date" && c.created_date ? String(c.created_date).slice(0, 10) : (c[k] ?? "");
        if (String(form[k] ?? "") !== String(orig)) body[k] = form[k];
      });
      if ((form.account_type || "individual") !== (c.account_type || "individual")) body.account_type = form.account_type;
      if ("deposits" in body) body.deposits = body.deposits === "" ? null : parseFloat(body.deposits);
      if ("balance" in body) body.balance = body.balance === "" ? null : parseFloat(body.balance);
      await api.patch(`/customers/${id}`, body);
      toast.success("Customer updated");
      qc.invalidateQueries({ queryKey: ["customer-profile", id] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const protectedFields = c?.protected_fields || [];

  const unlockField = async (field) => {
    try {
      await api.post(`/customers/${id}/unlock`, { field });
      toast.success(`"${field}" unlocked — the next import can update it`);
      qc.invalidateQueries({ queryKey: ["customer-profile", id] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (isLoading) {
    return <div className="space-y-4" data-testid="customer-profile-loading">{[...Array(3)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-md border border-border bg-[#E6EFF2]" />)}</div>;
  }
  if (!c) {
    return <div className="rounded-md border border-border bg-card p-8 text-center text-muted-foreground" data-testid="customer-profile-notfound">Customer not found.</div>;
  }

  const corp = c.account_type === "company";
  const txTotal = txs.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="customer-profile-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="back-from-profile"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="brand-gradient flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-[#0e2e3d]">
            {corp ? <Building2 className="h-6 w-6" /> : <User className="h-6 w-6" />}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="profile-name">{c.name || "—"}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className={corp ? "bg-[#5B6ABF]/15 text-[#4A57A3] hover:bg-[#5B6ABF]/15" : "bg-[#2b9bb7]/12 text-[#22798f] hover:bg-[#2b9bb7]/12"}>
                {corp ? "Corporate" : "Individual"}
              </Badge>
              <Badge variant="secondary" className={c.kyc_verified ? "bg-[#326F4B]/12 text-[#326F4B] hover:bg-[#326F4B]/12" : "bg-[#D68C3C]/15 text-[#A7773F] hover:bg-[#D68C3C]/15"}>
                {c.kyc_status || (c.kyc_verified ? "Verified" : "Pending")}
              </Badge>
              <span className="font-mono text-xs text-muted-foreground">{c.user_id}</span>
            </div>
          </div>
        </div>
        {isAdmin && dirty && (
          <Button onClick={save} disabled={busy} data-testid="profile-save">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
          </Button>
        )}
      </div>

      {/* Editable details */}
      <section className="rounded-md border border-border bg-card p-5">
        <h3 className="mb-1 font-heading text-lg font-medium">Details</h3>
        <p className="mb-4 text-xs text-muted-foreground">
          {isAdmin ? "Edit any field directly — no need to fix the Excel and re-import. Changed fields get locked (🔒) so imports never overwrite your corrections." : "Read-only view."}
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map(([key, label, type]) => (
            <div key={key} className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">{label}</Label>
                {protectedFields.includes(key) && (
                  <button
                    type="button"
                    onClick={() => isAdmin && unlockField(key)}
                    title={isAdmin ? "Corrected in portal — imports won't overwrite it. Click to unlock." : "Corrected in portal — imports won't overwrite it."}
                    data-testid={`lock-${key}`}
                    className="text-[#A7773F] transition-colors hover:text-destructive"
                  >
                    <Lock className="h-3 w-3" />
                  </button>
                )}
              </div>
              <Input
                type={type}
                value={form[key] ?? ""}
                disabled={!isAdmin}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                data-testid={`profile-field-${key}`}
              />
            </div>
          ))}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs">Account Type</Label>
              {protectedFields.includes("account_type") && (
                <button
                  type="button"
                  onClick={() => isAdmin && unlockField("account_type")}
                  title="Corrected in portal — imports won't overwrite it. Click to unlock."
                  data-testid="lock-account_type"
                  className="text-[#A7773F] transition-colors hover:text-destructive"
                >
                  <Lock className="h-3 w-3" />
                </button>
              )}
            </div>
            <select
              value={form.account_type ?? "individual"}
              disabled={!isAdmin}
              onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value }))}
              data-testid="profile-field-account_type"
              className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring disabled:opacity-60"
            >
              <option value="individual">Individual</option>
              <option value="company">Corporate</option>
            </select>
          </div>
        </div>
        {isAdmin && dirty && (
          <div className="mt-4 flex justify-end">
            <Button onClick={save} disabled={busy} data-testid="profile-save-bottom">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
            </Button>
          </div>
        )}
      </section>

      {/* Transactions */}
      <section className="rounded-md border border-border bg-card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-heading text-lg font-medium"><Receipt className="h-5 w-5 text-[#2b9bb7]" /> Transactions</h3>
          <span className="text-sm text-muted-foreground">{fmtNum(txs.length)} transaction(s) · volume {fmtCur(txTotal)}</span>
        </div>
        {txs.length === 0 ? (
          <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground" data-testid="profile-tx-empty">
            No transactions for this account yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm" data-testid="profile-tx-table">
              <thead>
                <tr className="border-b border-border bg-[#EDF3F5] text-left">
                  {["Date", "Transaction ID", "Provider", "Direction", "Amount", "Fee", "Status"].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i >= 4 && i <= 5 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txs.map((t, i) => (
                  <tr key={t.id} className="border-b border-border" data-testid={`profile-tx-row-${i}`}>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(t.created_at)}</td>
                    <td className="max-w-[200px] truncate px-4 py-3 font-mono text-xs text-muted-foreground">{t.tx_id}</td>
                    <td className="px-4 py-3 font-mono text-xs">{t.provider || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.direction || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtAmt(t.amount, t.currency)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtAmt(t.fee_amount, t.currency)}</td>
                    <td className="px-4 py-3">{t.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
